import { IssueCaseDoc, OrderDoc } from '@gooduncles/gu-app-schema';
import { Tabs } from 'antd';
import { FC } from 'react';

import { useRecentValidIssueCases } from 'src/hooks/subscription/useRecentValidIssueCases';
import { useRecentValidOrders } from 'src/hooks/subscription/useRecentValidOrders';

import RecentIssueCaseList from 'src/components/Crm/CrmBoard/components/StoreSection/RecentIssueCaseList';
import RecentOrderList from 'src/components/Crm/CrmBoard/components/StoreSection/RecentOrderList';

import styles from './CaseSourcePicker.module.scss';

interface CaseSourcePickerProps {
  storeId: string | null;
  linkedOrder: OrderDoc | null;
  linkedIssueCase: IssueCaseDoc | null;
  onSelectOrder: (order: OrderDoc | null) => void;
  onSelectIssueCase: (issueCase: IssueCaseDoc | null) => void;
}

/**
 * 케이스 등록 시 연결할 주문/이슈를 고르는 우측 패널.
 * 목록 UI(RecentOrderList/RecentIssueCaseList)와 데이터 소스(useRecentValid*)를
 * 채팅방 StoreInfoPanel 과 공유한다.
 */
const CaseSourcePicker: FC<CaseSourcePickerProps> = ({
  storeId,
  linkedOrder,
  linkedIssueCase,
  onSelectOrder,
  onSelectIssueCase,
}) => {
  const { orders, isLoading: ordersLoading } = useRecentValidOrders(storeId);
  const { issueCases, isLoading: issuesLoading } = useRecentValidIssueCases(storeId);

  // 케이스 원천(source)은 단일 필드라 주문/이슈는 상호 배타 — 하나를 고르면 다른 하나를 해제, 재클릭 시 해제.
  const handleOrderSelect = (order: OrderDoc) => {
    if (linkedOrder?._id === order._id) {
      onSelectOrder(null);
    } else {
      onSelectOrder(order);
      onSelectIssueCase(null);
    }
  };

  const handleIssueCaseSelect = (issueCase: IssueCaseDoc) => {
    if (linkedIssueCase?._id === issueCase._id) {
      onSelectIssueCase(null);
    } else {
      onSelectIssueCase(issueCase);
      onSelectOrder(null);
    }
  };

  const tabItems = [
    {
      key: 'order',
      label: '주문',
      children: (
        <RecentOrderList
          orders={orders}
          isLoading={ordersLoading}
          selectedOrderId={linkedOrder?._id ?? null}
          onOrderSelect={handleOrderSelect}
        />
      ),
    },
    {
      key: 'issue',
      label: '이슈',
      children: (
        <RecentIssueCaseList
          issueCases={issueCases}
          isLoading={issuesLoading}
          selectedIssueCaseId={linkedIssueCase?._id ?? null}
          onIssueCaseSelect={handleIssueCaseSelect}
        />
      ),
    },
  ];

  return (
    <div className={styles.picker}>
      <div className={styles.sectionLabel}>연결할 주문/이슈 (선택)</div>
      <Tabs defaultActiveKey='order' className={styles.tabs} items={tabItems} />
    </div>
  );
};

export default CaseSourcePicker;
